import { Box, Button, Divider, List, ListItem, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { getUserEmail } from '../../../helpers/authUser';

const useStyles = makeStyles((theme) => ({
    titlePlan: {
        fontFamily: 'Lato',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        color: theme.colors.cyan[300],
    },
    featureList: {
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '1rem',
        color: '#666666',
    },
    featureCount: {
        fontFamily: 'Poppins',
        fontWeight: 'bold',
        fontSize: '1rem',
        color: theme.colors.primary[500],
    },
    discountChip: {
        height: 36,
        width: 96,
        borderRadius: 32,
        borderWidth: 3,
        padding: 8,
        fontSize: 18,
        fontWeight: 'bold',
        textTransform: 'none',
        background: theme.colors.neutral[50],
        color: theme.colors.cyan[400],
    },
    rateText: {
        fontFamily: 'Lato',
        fontSize: '1.25rem',
        fontWeight: 500,
        color: theme.colors.neutral[600],
    },
    pilotDiscount: {
        padding: '2px 24px',
        fontFamily: 'Lato',
        fontWeight: 600,
        fontSize: '0.95rem',
        textAlign: 'right',
        border: `1px solid ${theme.colors.orange[200]}`,
        backgroundColor: theme.colors.orange[100],
        color: theme.colors.warning[700],
        borderRadius: 16,
    },
    discountedPriceText: {
        paddingBottom: 4,
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '2rem',
        color: '#000',
        textAlign: 'right',
    },
    originalPriceText: {
        position: 'relative',
        display: 'inline-block',
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '2rem',
    },
    originalPriceSlash: {
        position: 'relative',
        width: '120%',
        height: 0,
        top: -24,
        left: -8,
        borderTop: `3px solid ${theme.colors.neutral[300]}`,
    },
    planButton: {
        width: 200,
        height: 50,
        borderRadius: 32,
        borderWidth: 3,
        textTransform: 'none',
        padding: '8px 24px',
        fontFamily: 'Lato',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#1494aa',
        backgroundColor: '#def1f5',
        "&:hover": {
            backgroundColor: 'rgba(222, 241, 245, 0.8)',
        }
    },
    daysLeft: {
        marginTop: 8,
        fontSize: '1rem',
        fontFamily: 'Inter',
        fontWeight: '500',
        color: theme.colors.warning[500],
    },
    url: {
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: '500',
        color: theme.colors.cyan[400],
        textDecoration: 'none',
        "&:hover": {
            textDecoration: 'none !important',
        }
    },
    contact: {
        height: 120,
        padding: 16,
        border: `1px solid ${theme.colors.neutral[200]}`,
        backgroundColor: theme.colors.neutral[100],
        borderRadius: 16,
    },
}));

const ProPlan: React.FC<{
    selected: boolean,
    admin: string,
    daysLeft?: number,
}> = ({ selected, admin, daysLeft  }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [isAdmin, setAdmin] = useState<boolean>(false);

    useEffect(() => {
        if (user && admin)
            setAdmin(admin === getUserEmail(user).toLowerCase());
    }, [admin, user]);

    return (<Box position="relative" width="100%">
        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" p={2} height="180px">
            <Typography className={classes.titlePlan}>{'Pro'}</Typography>
            <Stack direction="row" spacing={4} alignItems="center" justifyContent="flex-start" width="100%">
                {!!daysLeft ? (<>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Typography component="div" className={classes.originalPriceText} color="#b8b8b8">
                            {'$99'}
                            <Typography component="div" className={classes.originalPriceSlash} />
                        </Typography>
                        <Typography component="span" className={classes.discountedPriceText}>
                            {'$50'}
                        </Typography>
                    </Stack>
                </>) : (
                    <Typography className={classes.originalPriceText} color="#000">{'$99'}</Typography>
                )}
            </Stack>
            {!!daysLeft && (
                <Typography className={classes.pilotDiscount}>
                    {'Pilot discount'}
                </Typography>
            )}
            <Typography className={classes.rateText}>{'per month'}</Typography>
        </Stack>
        <Divider />
        <Stack alignItems="flex-start" p={1} height="200px" mt={1}>
            <Typography fontFamily="Inter" fontWeight="bold" fontSize="1.1rem">{'What\'s included:'}</Typography>
            <List sx={{ width: '100%', margin: '0 32px', listStyleType: 'disc' }}>
                <ListItem sx={{ py: '2px', paddingLeft: '0px !important', display: 'list-item' }}>
                    <Typography className={classes.featureList} display="inline"> {'Up to'} </Typography>
                    <Typography className={classes.featureCount} display="inline">
                        {`1 user`}
                    </Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: '0px !important', display: 'list-item' }}>
                    <Typography className={classes.featureCount} display="inline">
                        {`30 emails`}
                    </Typography>
                    <Typography className={classes.featureList} display="inline">{' per month '}</Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: 'unset', display: 'list-item' }}>
                    <Typography className={classes.featureList}>{'Connect to external tools'}</Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: 'unset', display: 'list-item' }}>
                    <Typography className={classes.featureList}>{'Unlimited custom data fields'}</Typography>
                </ListItem>
            </List>
        </Stack>
        {!isAdmin && (<>
            <Stack className={classes.contact} alignItems="center" justifyContent="center">
                <Typography color="#666666" textAlign="center" fontFamily="Inter" fontSize="0.95rem" fontWeight="500">
                    {'Contact your account administrator'}<br/>{'to manage your subscription '}
                </Typography>
                <Typography
                    component="a"
                    className={classes.url}
                    href={'mailto:' + admin}
                    target="_blank"
                    rel="noopener noreferrer"
                >({admin})</Typography>
            </Stack>
        </>)}
        {(isAdmin && !selected) && (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="120px">
                <Button variant="contained"
                    className={classes.planButton}>
                    {'Contact us'}
                </Button>
            </Stack>
        </>)}
    </Box>);
}

export default ProPlan;